import React, { useState, useEffect, useMemo, useRef } from "react";
import StoreSidebar from "../components/StoreSidebar";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firebase";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  Package, FileText, Users, Eye, BarChart3,
  ShoppingCart, Clock3, CheckCircle2, AlertTriangle,
  IndianRupee, Plus, MapPin, Calendar as CalendarIcon,
  Filter, User as UserIcon, Tag, ChevronLeft, ChevronRight, X, Download
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function StoreDashboard() {
  const navigate = useNavigate();
  const dashboardRef = useRef(null);

  // --- SINGLE TENANT BRANCH CONTEXT ---
  const [activeBranchId, setActiveBranchId] = useState(""); 
  const [storeName, setStoreName] = useState("");

  // --- DATA STATES ---
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  
  // --- FILTER STATES ---
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [salespersonFilter, setSalespersonFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");

  // --- PAGINATION STATES ---
  const [closingStockPage, setClosingStockPage] = useState(1);
  const recordsPerPage = 9;

  // --- VIEW ALL MODAL STATE ---
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);

  // --- FETCH ONLY THE LOGGED-IN USER'S STORE ---
  useEffect(() => {
    const auth = getAuth();
    let unsubStore = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsDataLoading(true);
        const storeQuery = query(collection(db, "stores"), where("linkedAuthUid", "==", user.uid));
        
        unsubStore = onSnapshot(storeQuery, (snapshot) => {
          if (!snapshot.empty) {
            const storeDoc = snapshot.docs[0];
            setActiveBranchId(storeDoc.id);
            setStoreName(storeDoc.data().storeId || storeDoc.data().name || "Authorized Branch");
          } else {
            setIsDataLoading(false);
          }
        });
      } else {
        setIsDataLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubStore) unsubStore();
    };
  }, []);

  // --- SYSTEM REAL-TIME LISTENERS ---
  useEffect(() => {
    if (!activeBranchId) return;

    const unsubSales = onSnapshot(collection(db, "stores", activeBranchId, "sales"), (snapshot) => {
      setSales(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const unsubCustomers = onSnapshot(collection(db, "stores", activeBranchId, "customers"), (snapshot) => {
      setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const unsubInventory = onSnapshot(collection(db, "stores", activeBranchId, "inventory"), (snapshot) => {
      setInventory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIsDataLoading(false);
    });

    return () => { unsubSales(); unsubCustomers(); unsubInventory(); };
  }, [activeBranchId]);

  // --- METRIC OPTIONS EXTRACTORS ---
  const uniqueSalespeople = useMemo(() => {
    return Array.from(new Set(sales.map(s => s.salesperson).filter(Boolean)));
  }, [sales]);

  const uniqueCategories = useMemo(() => {
    return Array.from(new Set(inventory.map(i => i.productType || i.category).filter(Boolean)));
  }, [inventory]);

  // --- ENGINE: FILTER DATA BY CALENDAR, REP, & CATEGORY ---
  const filteredSalesData = useMemo(() => {
    const todayStr = new Date().toISOString().split("T")[0];
    
    return sales.filter(s => {
      const orderDate = s.timestamp?.split("T")[0];
      if (!orderDate) return false;

      let dateMatch = false;
      if (startDate || endDate) {
        dateMatch = true;
        if (startDate && orderDate < startDate) dateMatch = false;
        if (endDate && orderDate > endDate) dateMatch = false;
      } else {
        dateMatch = orderDate === todayStr;
      }
      if (!dateMatch) return false;

      if (salespersonFilter && s.salesperson !== salespersonFilter) return false;

      if (categoryFilter) {
        const hasMatchingCategory = s.items?.some(item => 
          item.productType === categoryFilter || 
          item.category === categoryFilter ||
          item.productName?.toLowerCase().includes(categoryFilter.toLowerCase())
        );
        if (!hasMatchingCategory) return false;
      }

      return true;
    });
  }, [sales, startDate, endDate, salespersonFilter, categoryFilter]);

  // --- KPI MATRIX CALCULATIONS ---
  const liveKPI = useMemo(() => {
    const totalSalesSum = filteredSalesData.reduce((sum, s) => sum + (Number(s.financials?.netPayable) || 0), 0);
    const pendingCount = filteredSalesData.filter(s => s.orderStatus === "Pending").length;
    const completedCount = filteredSalesData.filter(s => s.orderStatus === "Completed").length;
    const customerCount = customers.length; 

    return { totalSalesSum, pendingCount, completedCount, customerCount };
  }, [filteredSalesData, customers]);

  // --- QUANTITY AND VALUE CALCULATION ACCUMULATORS ---
  const totalStockQuantity = useMemo(() => {
    return inventory.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0);
  }, [inventory]);

  const totalInventoryValuation = useMemo(() => {
    return inventory.reduce((acc, item) => acc + ((Number(item.quantity) || 0) * (Number(item.unitPrice || item.retailPrice || item.purchasePrice || 0))), 0);
  }, [inventory]);

  // --- DYNAMIC MATRIX BREAKDOWN GRAPHICS ENG ---
  const productTypeSummaryTable = useMemo(() => {
    const summary = {};
    inventory.forEach(item => {
      const pType = item.productType || "General";
      const qty = Number(item.quantity) || 0;
      const value = qty * (Number(item.retailPrice || item.unitPrice || 0));
      
      if (!summary[pType]) {
        summary[pType] = { quantity: 0, stockValue: 0 };
      }
      summary[pType].quantity += qty;
      summary[pType].stockValue += value;
    });
    return Object.entries(summary).map(([type, data]) => ({ type, ...data }));
  }, [inventory]);

  // --- CRITICAL ALERT ENGINE (FIX: Quantity = 0 items completely excluded) ---
  const lowStockAlerts = useMemo(() => {
    return inventory.filter(item => {
      const qty = Number(item.quantity) || 0;
      return qty > 0 && qty < 5;
    });
  }, [inventory]);

  // --- PAGINATION SUBSET COMPILER ---
  const paginatedInventoryData = useMemo(() => {
    const offset = (closingStockPage - 1) * recordsPerPage;
    return inventory.slice(offset, offset + recordsPerPage);
  }, [inventory, closingStockPage]);

  const maxClosingStockPages = Math.ceil(inventory.length / recordsPerPage) || 1;

  // View All inventory collection filtering out empty 0 stock
  const activeViewAllInventory = useMemo(() => {
    return inventory.filter(item => (Number(item.quantity) || 0) > 0);
  }, [inventory]);

  // --- FILTERED ORDERS LIST ENGINE ---
  const filteredOrdersTable = useMemo(() => {
    const ordersMap = filteredSalesData.map(s => ({
      id: s.invoiceNo || s.id?.slice(-6).toUpperCase(),
      customer: s.customer?.name || "Walk-in Client",
      amount: s.financials?.netPayable || 0,
      status: s.orderStatus || "Completed",
      type: s.items?.[0]?.productName ? `${s.items[0].productName} ${s.items.length > 1 ? `+${s.items.length - 1}` : ""}` : "Optical Items"
    }));

    if (orderStatusFilter === "all") return ordersMap.slice(0, 10);
    return ordersMap.filter(o => o.status.toLowerCase() === orderStatusFilter).slice(0, 10);
  }, [filteredSalesData, orderStatusFilter]);

  // --- BUSINESS LOGIC PORTING: HIGH-FIDELITY PDF ENGINE ---
  const exportHighFidelityDashboardPDF = async () => {
    const doc = new jsPDF("p", "mm", "a4");
    const timestampString = new Date().toLocaleString();

    const drawHeaderAndFooter = (pdfInstance, titleString, pageNum) => {
      pdfInstance.setFillColor(30, 41, 59); // Slate 800
      pdfInstance.rect(0, 0, 210, 24, "F");

      pdfInstance.setFont("helvetica", "bold");
      pdfInstance.setFontSize(15);
      pdfInstance.setTextColor(255, 255, 255);
      pdfInstance.text(`SHRUTI ERP BUSINESS REPORT - ${titleString.toUpperCase()}`, 14, 11);

      pdfInstance.setFont("helvetica", "normal");
      pdfInstance.setFontSize(8);
      pdfInstance.setTextColor(148, 163, 184);
      pdfInstance.text(`Branch Node Profile ID Location Scope Context: ${storeName.toUpperCase()}`, 14, 18);

      pdfInstance.setTextColor(255, 255, 255);
      pdfInstance.text(`Generated: ${timestampString}`, 150, 11);

      pdfInstance.setFillColor(37, 99, 235); // Blue 600 line
      pdfInstance.rect(0, 24, 210, 1.5, "F");

      pdfInstance.setFontSize(8);
      pdfInstance.setTextColor(100, 116, 139);
      pdfInstance.text(`Page ${pageNum}`, 190, 288);
    };

    // PAGE 1: Overview Summary Stats
    drawHeaderAndFooter(doc, "Executive Summary Overview", 1);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(30, 41, 59);
    doc.text("1. CORE LOGISTICS PERFORMANCE METRICS", 14, 36);

    const summaryRows = [
      ["Revenue Focus Filter Scope Tracker", startDate || endDate ? "Custom Dynamic Configuration Filter" : "Today Sync Line Feed"],
      ["Net Sales Calculated Revenue Matrix", `INR ${liveKPI.totalSalesSum.toLocaleString()}`],
      ["Completed Log Order Count Invoices", String(liveKPI.completedCount)],
      ["Pending State Pipeline Order Invoices", String(liveKPI.pendingCount)],
      ["Total CRM Active Registered Customers Profiles", String(liveKPI.customerCount)],
      ["Cumulative Store Physical Item Inventory Units", `${totalStockQuantity.toLocaleString()} Units`],
      ["Net Total Asset Stock Valuation (Retail Basis)", `INR ${totalInventoryValuation.toLocaleString()}`]
    ];

    autoTable(doc, {
      startY: 40,
      head: [["Performance Dynamic Property Category", "Computed Architecture State Matrix Data"]],
      body: summaryRows,
      theme: "grid",
      headStyles: { fillColor: [30, 41, 59] }
    });

    // Product Type Valuation Summary Matrix
    let currentY = doc.lastAutoTable.finalY + 12;
    doc.text("2. PRODUCT MODULE MODEL DISTRIBUTION MAP", 14, currentY);
    
    const typeRowsPDF = productTypeSummaryTable.map(item => [
      item.type,
      `${item.quantity.toLocaleString()} Units`,
      `INR ${item.stockValue.toLocaleString()}`
    ]);

    autoTable(doc, {
      startY: currentY + 4,
      head: [["Product Type Descriptor Category", "On-Hand Balance Density Units", "Total Valuation Accumulator Amount (Retail)"]],
      body: typeRowsPDF,
      theme: "striped",
      headStyles: { fillColor: [37, 99, 235] }
    });

    // PAGE 2: Exceptions Stock Alerts & Balances
    doc.addPage();
    drawHeaderAndFooter(doc, "Closing Structural Core Balances", 2);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("3. SYSTEM EXCEPTION WATCH WARNING LOW-STOCK CHANNELS", 14, 36);

    const alertsRowsPDF = lowStockAlerts.map(item => [
      item.productCode || item.sku || "N/A",
      item.productName || "Product",
      item.productType || "General",
      `${item.quantity} Units On Hand`
    ]);

    autoTable(doc, {
      startY: 40,
      head: [["SKU Code", "Product Label Name Structure", "Component Classifier Type", "Exception Boundaries Status Current"]],
      body: alertsRowsPDF.length > 0 ? alertsRowsPDF : [["-", "Optimal Framework Environment: Zero Alerts Detected", "-", "-"]],
      theme: "grid",
      headStyles: { fillColor: [239, 68, 68] }
    });

    doc.save(`ERP_Operational_Branch_SnapshotAudit_${storeName.replace(/\s+/g, '_')}.pdf`);
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans antialiased selection:bg-blue-200">
      <StoreSidebar />

      {/* MAIN CONTAINER CONTENT VIEWPORT */}
      <div ref={dashboardRef} className="flex-1 p-6 lg:p-10 space-y-6 overflow-x-hidden">

        {/* HEADER BLOCK */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between p-6 bg-white border border-slate-200 rounded-3xl gap-4 shadow-sm"
        >
          <div>
            <h1 className="text-2xl font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <BarChart3 size={24} className="text-blue-600" /> Overview & Analytics
            </h1>
            <div className="flex items-center gap-1.5 mt-1.5 text-sm text-slate-500 font-medium">
              <MapPin size={14} className="text-blue-500" />
              <span>Operating Branch: </span>
              <span className="bg-blue-50 text-blue-700 font-black px-3 py-1 rounded-xl border border-blue-100 text-xs tracking-widest uppercase shadow-sm">
                {storeName || "Loading Parameters..."}
              </span>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <button 
              onClick={exportHighFidelityDashboardPDF}
              className="px-4 py-3 bg-slate-800 hover:bg-slate-900 text-white rounded-xl font-bold text-xs uppercase tracking-wider transition-colors shadow-sm flex items-center gap-2"
            >
              <Download size={14} /> Export Report PDF
            </button>
            <button onClick={() => navigate('/store/sales')} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-colors shadow-sm shadow-blue-200 flex items-center gap-2">
              <Plus size={18} /> Create New Invoice
            </button>
          </div>
        </motion.div>

        {/* ADVANCED FILTER DESK */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <Filter size={18} className="text-blue-500" />
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-600">Master Data Filters</h2>
            <span className="text-[10px] bg-blue-50 text-blue-600 font-bold px-2 py-0.5 rounded-full ml-2">Defaults to Today</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-600">
              <CalendarIcon size={14} className="text-slate-400"/>
              <span>From:</span>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent outline-none font-mono cursor-pointer w-full text-slate-800" />
            </div>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-600">
              <CalendarIcon size={14} className="text-slate-400"/>
              <span>To:</span>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent outline-none font-mono cursor-pointer w-full text-slate-800" />
            </div>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-600">
              <UserIcon size={14} className="text-slate-400"/>
              <select value={salespersonFilter} onChange={e => setSalespersonFilter(e.target.value)} className="bg-transparent outline-none cursor-pointer w-full text-slate-800">
                <option value="">All Sales Reps</option>
                {uniqueSalespeople.map(rep => <option key={rep} value={rep}>{rep}</option>)}
              </select>
            </div>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-600">
              <Tag size={14} className="text-slate-400"/>
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="bg-transparent outline-none cursor-pointer w-full text-slate-800">
                <option value="">All Categories</option>
                {uniqueCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* BRIGHT OVERSIZED KPI METRICS STRIP GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {[
            { title: startDate || endDate ? "Filtered Revenue" : "Today's Revenue", value: `₹${liveKPI.totalSalesSum.toLocaleString()}`, icon: IndianRupee, bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-600", iconBg: "bg-emerald-100" },
            { title: "Pending Invoices", value: liveKPI.pendingCount, icon: Clock3, bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-600", iconBg: "bg-amber-100" },
            { title: "Completed Invoices", value: liveKPI.completedCount, icon: CheckCircle2, bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-600", iconBg: "bg-blue-100" },
            { title: "Total CRM Customers", value: liveKPI.customerCount, icon: Users, bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-600", iconBg: "bg-purple-100" }
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={i}
                whileHover={{ scale: 1.02, y: -4 }}
                className={`${item.bg} border ${item.border} rounded-2xl p-6 shadow-sm flex flex-col justify-between h-36 relative overflow-hidden group`}
              >
                <div className="flex justify-between items-start font-bold">
                  <span className={`text-xs uppercase tracking-wider ${item.text} opacity-80`}>{item.title}</span>
                  <div className={`p-2 rounded-xl ${item.iconBg} ${item.text}`}><Icon size={16} /></div>
                </div>
                <h2 className={`text-3xl font-mono font-black tracking-tight ${item.text}`}>{item.value}</h2>
              </motion.div>
            );
          })}
        </div>

        {/* ALERTS & SUMMARY MAPS ROW */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* CRITICAL STOCK ALERTS (FIXED: Zero stock items excluded completely) */}
          <div className="lg:col-span-1 bg-rose-50 border border-rose-200 rounded-3xl p-6 shadow-sm flex flex-col">
            <h3 className="text-sm font-black uppercase tracking-wider text-rose-600 flex items-center gap-2 mb-4">
              <AlertTriangle size={18} /> Critical Stock Alerts
            </h3>
            <div className="flex-1 overflow-y-auto space-y-2 pr-2 custom-scrollbar max-h-[320px]">
              {isDataLoading ? (
                <div className="text-center py-4 text-xs font-bold text-rose-400">Syncing Parameters...</div>
              ) : lowStockAlerts.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-rose-400/70 text-center space-y-2 py-8">
                  <CheckCircle2 size={32} />
                  <p className="text-xs font-bold uppercase tracking-wider">All Stock Optimal</p>
                </div>
              ) : (
                lowStockAlerts.map(item => (
                  <div key={item.id} className="bg-white p-3 rounded-xl border border-rose-100 shadow-sm flex justify-between items-center">
                    <div className="flex flex-col min-w-0 flex-1 pr-2">
                      <span className="text-xs font-bold text-slate-700 truncate">{item.productName || "Optical Item"}</span>
                      <span className="text-[9px] font-mono font-bold text-slate-400 truncate">{item.productCode || item.sku || "No SKU"}</span>
                    </div>
                    <span className="text-[10px] font-black font-mono bg-rose-100 text-rose-600 px-2 py-1 rounded-md shrink-0">
                      {item.quantity} LEFT
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* REQUIREMENT UPDATE: PRODUCT TYPE REVENUE / STOCK VALUE SUMMARY TABLE */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col overflow-hidden">
            <h3 className="text-sm font-black uppercase tracking-wider text-slate-600 flex items-center gap-2 mb-3">
              <Package size={18} className="text-blue-500" /> Stock Level Distribution Summary
            </h3>
            <div className="overflow-x-auto flex-1 text-[11px] font-medium custom-scrollbar max-h-[320px]">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[9px]">
                    <th className="p-3 pl-4">Product Type Identifier</th>
                    <th className="p-3 text-center">Total Quantity</th>
                    <th className="p-3 text-right pr-4">Total Stock Value (Retail basis)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {productTypeSummaryTable.map((row) => (
                    <tr key={row.type} className="hover:bg-slate-50/70 transition-colors">
                      <td className="p-3 pl-4 font-bold text-slate-800 uppercase">{row.type}</td>
                      <td className="p-3 text-center font-mono font-black text-blue-600">{row.quantity.toLocaleString()}</td>
                      <td className="p-3 text-right font-mono font-black text-emerald-600 pr-4">₹{row.stockValue.toLocaleString()}</td>
                    </tr>
                  ))}
                  {productTypeSummaryTable.length === 0 && (
                    <tr><td colSpan={3} className="p-6 text-center text-slate-400 font-bold uppercase">No Storage Framework Found Mapped</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* COMPREHENSIVE PHYSICAL STOCK LEDGER CARD */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-sm font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
                <Package size={18} className="text-blue-500" /> Comprehensive Physical Stock Ledger
              </h2>
              <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Real-time balances for context scoped login station identity</p>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-[11px] font-black text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 flex items-center gap-3">
                <div>TTL BAL: <span className="text-blue-600 font-mono font-black">{totalStockQuantity.toLocaleString()} Units</span></div>
                <div className="w-px h-3 bg-slate-300"></div>
                <div>TTL VAL: <span className="text-emerald-600 font-mono font-black">₹{totalInventoryValuation.toLocaleString()}</span></div>
              </div>
              <button 
                onClick={() => setIsViewAllOpen(true)}
                className="px-4 py-2 bg-blue-50 border border-blue-200 hover:bg-blue-600 text-blue-600 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-sm flex items-center gap-1"
              >
                <Eye size={14} /> View All
              </button>
            </div>
          </div>

          <div className="overflow-x-auto text-[11px] font-medium custom-scrollbar">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 font-bold text-slate-500 uppercase tracking-wider text-[10px]">
                  <th className="p-3 pl-4">SKU / Code</th>
                  <th className="p-3">Product Name</th>
                  <th className="p-3">Product Type</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">Brand</th>
                  <th className="p-3 text-center">Qty Balance</th>
                  <th className="p-3 text-right">Unit Price</th>
                  <th className="p-3 text-right pr-4">Stock Valuation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {isDataLoading ? (
                  <tr><td colSpan={8} className="p-6 text-center text-slate-400 font-bold uppercase tracking-widest">Processing Layer Query Feed...</td></tr>
                ) : paginatedInventoryData.length === 0 ? (
                  <tr><td colSpan={8} className="p-8 text-center text-slate-400 font-medium">Empty Frame State: No catalog entries found context profile location.</td></tr>
                ) : (
                  paginatedInventoryData.map((item) => {
                    const currentQty = Number(item.quantity) || 0;
                    const uPrice = Number(item.retailPrice || item.unitPrice || item.purchasePrice || 0);
                    const stockValuation = currentQty * uPrice;
                    return (
                      <tr key={item.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="p-3 pl-4 font-mono font-bold text-slate-500">{item.productCode || item.sku || "N/A"}</td>
                        <td className="p-3 font-bold text-slate-900">{item.productName || "Unnamed Model Entry"}</td>
                        <td className="p-3 text-slate-500 uppercase text-[10px] font-bold">{item.productType || "Standard"}</td>
                        <td className="p-3 text-slate-500">{item.category || "General"}</td>
                        <td className="p-3 text-slate-400 font-bold">{item.brand || "N/A"}</td>
                        <td className="p-3 text-center font-mono font-black">
                          <span className={currentQty === 0 ? "text-slate-300 line-through" : currentQty < 5 ? "text-rose-600 bg-rose-50 px-2 py-0.5 rounded" : "text-blue-600"}>
                            {currentQty}
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono font-bold text-slate-600">₹{uPrice.toLocaleString()}</td>
                        <td className="p-3 text-right font-mono font-black text-emerald-600 pr-4">₹{stockValuation.toLocaleString()}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* REQUIREMENT 3: DYNAMIC PAGINATION CONTROLS (CONDITIONAL ONLY IF RECORDS > 9) */}
          {inventory.length > recordsPerPage && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-100 text-[11px] font-bold text-slate-500">
              <div>Page Segments Indices: <span className="text-slate-800 font-black font-mono">{closingStockPage}</span> / <span className="text-slate-800 font-black font-mono">{maxClosingStockPages}</span> generated maps</div>
              <div className="flex items-center gap-1">
                <button 
                  disabled={closingStockPage === 1}
                  onClick={() => setClosingStockPage(prev => Math.max(prev - 1, 1))}
                  className="p-1.5 bg-slate-50 hover:bg-slate-100 disabled:opacity-40 border border-slate-200 rounded-lg transition-all text-slate-700"
                >
                  <ChevronLeft size={15} />
                </button>
                {Array.from({ length: maxClosingStockPages }, (_, idx) => idx + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setClosingStockPage(pageNum)}
                    className={`px-2.5 py-1 font-mono rounded-md transition-all ${closingStockPage === pageNum ? 'bg-blue-600 text-white shadow-sm border border-blue-600' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  >
                    {pageNum}
                  </button>
                ))}
                <button 
                  disabled={closingStockPage === maxClosingStockPages}
                  onClick={() => setClosingStockPage(prev => Math.min(prev + 1, maxClosingStockPages))}
                  className="p-1.5 bg-slate-50 hover:bg-slate-100 disabled:opacity-40 border border-slate-200 rounded-lg transition-all text-slate-700"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* MODULE LAUNCHERS MODULE MAP */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Inventory Warehouse", route: "/store/inventory", bg: "bg-blue-50 hover:bg-blue-100", border: "border-blue-200", icon: Package, text: "text-blue-600", desc: "Manage stock & items" },
            { title: "Billing Terminal", route: "/store/sales", bg: "bg-emerald-50 hover:bg-emerald-100", border: "border-emerald-200", icon: FileText, text: "text-emerald-600", desc: "New POS & Invoices" },
            { title: "Patient CRM", route: "/store/customers", bg: "bg-purple-50 hover:bg-purple-100", border: "border-purple-200", icon: Users, text: "text-purple-600", desc: "Profiles & History" }
          ].map((module, i) => {
            const Icon = module.icon;
            return (
              <motion.div
                key={i}
                whileHover={{ y: -2 }}
                onClick={() => navigate(module.route)}
                className={`cursor-pointer border ${module.border} rounded-2xl p-5 ${module.bg} flex items-center gap-4 transition-all shadow-sm group`}
              >
                <div className={`p-3 bg-white rounded-xl shadow-sm ${module.text}`}><Icon size={20} /></div>
                <div>
                  <h3 className={`font-black text-sm uppercase tracking-wider ${module.text}`}>{module.title}</h3>
                  <p className="text-xs text-slate-500 font-medium">{module.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ORDERS MANAGEMENT TABLE LEDGER */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-600 flex items-center gap-2">
              <ShoppingCart size={18} className="text-blue-500" /> Filtered Orders Ledger
            </h2>
            
            <div className="flex bg-slate-100 border border-slate-200 p-1 rounded-xl text-[10px] font-bold">
              {["all", "pending", "completed"].map(f => (
                <button 
                  key={f} 
                  onClick={() => setOrderStatusFilter(f)} 
                  className={`px-4 py-1.5 rounded-lg capitalize transition-all ${orderStatusFilter === f ? "bg-white text-blue-600 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-700"}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto text-[11px] font-medium">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 font-bold text-slate-500 uppercase tracking-wider">
                    <th className="p-4 pl-6">Invoice ID</th>
                    <th className="p-4">Customer Name</th>
                    <th className="p-4">Product Summary</th>
                    <th className="p-4">Net Amount</th>
                    <th className="p-4 pr-6">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {filteredOrdersTable.length === 0 ? (
                    <tr><td colSpan="5" className="p-8 text-center text-slate-500 font-medium">No orders matched the current dashboard filters.</td></tr>
                  ) : (
                    filteredOrdersTable.map((o) => (
                      <tr key={o.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="p-4 pl-6 font-mono font-bold text-blue-600 text-xs">#{o.id}</td>
                        <td className="p-4 font-bold text-slate-800 text-xs">{o.customer}</td>
                        <td className="p-4 text-slate-500 truncate max-w-[200px]">{o.type}</td>
                        <td className="p-4 font-mono font-bold text-slate-800 text-xs">₹{o.amount.toLocaleString()}</td>
                        <td className="p-4 pr-6 font-black uppercase text-[10px] tracking-wide">
                          <span className={`px-2 py-1 rounded-md ${o.status === "Completed" ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-amber-50 text-amber-600 border border-amber-200"}`}>
                            {o.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>

      {/* VIEW ALL POPUP RECONCILED SEGMENT MODULE */}
      <AnimatePresence>
        {isViewAllOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 font-sans">
            <motion.div 
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              className="bg-white w-full max-w-5xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[85vh]"
            >
              <div className="bg-slate-900 p-5 text-white flex items-center justify-between shrink-0">
                <div>
                  <h3 className="text-sm font-black uppercase tracking-wider flex items-center gap-2">
                    <Package size={16} className="text-blue-400" /> Full Operational Active Inventory Trace Matrix
                  </h3>
                  <p className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wider font-bold">Trace Balance Instances Total Logged: {activeViewAllInventory.length} lines entries</p>
                </div>
                <button onClick={() => setIsViewAllOpen(false)} className="p-1 hover:bg-rose-600 rounded-lg text-slate-400 hover:text-white transition-all">
                  <X size={18} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto text-[11px] custom-scrollbar">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-100 uppercase tracking-wider font-bold text-slate-600 text-[9px] sticky top-0 border-b border-slate-200 z-10">
                    <tr>
                      <th className="p-3 pl-5">SKU / Code ID</th>
                      <th className="p-3">Designation Label Description</th>
                      <th className="p-3">Model Type</th>
                      <th className="p-3">Category</th>
                      <th className="p-3">Brand Identity</th>
                      <th className="p-3 text-center">Quantity Balance</th>
                      <th className="p-3 text-right pr-5">Retail Price Valuation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                    {activeViewAllInventory.map(item => (
                      <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                        <td className="p-3 pl-5 font-mono text-slate-500 font-bold">{item.productCode || item.sku || "N/A"}</td>
                        <td className="p-3 font-black text-slate-800">{item.productName || "Product Reference Item"}</td>
                        <td className="p-3 uppercase text-[9px] font-bold text-slate-400">{item.productType || "Standard"}</td>
                        <td className="p-3 text-slate-500">{item.category || "General"}</td>
                        <td className="p-3 text-slate-400 font-bold">{item.brand || "N/A"}</td>
                        <td className="p-3 text-center font-mono font-black text-blue-600">{item.quantity}</td>
                        <td className="p-3 text-right font-mono font-black text-emerald-600 pr-5">₹{(Number(item.retailPrice || item.unitPrice || 0)).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}